import { createFileRoute, type ErrorComponentProps, SearchParamError, useRouter } from '@tanstack/react-router';
import z from 'zod';
import { Flex } from '@/components/Flex';
import { InvalidRequest } from '@/components/InvalidRequest';
import { Page } from '@/components/Page';
import { RouteError } from '@/components/RouteError';
import { Section } from '@/components/Section';
import { fetchReportingTask } from '@/queries/reporting';
import * as shared from '../tasks.css';
import { Artifacts, ArtifactsSkeleton } from './-fragments/Artifacts';
import { CrossTabulationSettings, CrossTabulationSettingsSkeleton } from './-fragments/CrossTabulationSettings';
import { InputFiles, InputFilesSkeleton } from './-fragments/InputFiles';
import { StatusHistory, StatusHistorySkeleton } from './-fragments/StatusHistory';
import { SurveyImport } from './-fragments/SurveyImport';
import { TaskOverview, TaskOverviewSkeleton } from './-fragments/TaskOverview';
import { useTaskStatusHistory } from './hooks';

export const Route = createFileRoute('/system/_auth/reporting/tasks/$taskId/')({
    component: SystemShowReportingTaskRoute,
    loader: async ({ params }) => fetchReportingTask(params.taskId),
    pendingComponent: SystemShowReportingTaskRouteSkeleton,
    errorComponent: SystemShowReportingTaskRouteError,
    validateSearch: z.object({
        new: z.boolean().optional(),
    }),
});

function SystemShowReportingTaskRouteError({ error, reset }: ErrorComponentProps) {
    if (error instanceof SearchParamError) {
        return <InvalidRequest title="タスク詳細" kind="system" />;
    }

    return <RouteError kind="system" title="タスク詳細" onRetry={reset} />;
}

function SystemShowReportingTaskRoute() {
    const task = Route.useLoaderData();
    const { taskId } = Route.useParams();
    const { new: isNewTask } = Route.useSearch();
    const router = useRouter();

    const statusHistoryQuery = useTaskStatusHistory(taskId);

    const isSurveyTask = task.type.id === 'NEW_SURVEY' || task.type.id === 'OVERWRITE_SURVEY';
    const hasSucceeded = statusHistoryQuery.data?.at(0)?.code.id === 'ETL_SUCCESS';
    const canImport = isSurveyTask && hasSucceeded;

    return (
        <Page.Root>
            <Page.Header title="タスク詳細" />

            <Page.Content gap="5">
                <Page.BackLink to="/system/reporting" label="タスク一覧に戻る" forceNavigate={isNewTask} />

                <Section>
                    <Flex direction="column" gap="5">
                        <TaskOverview task={task} />
                        <hr className={shared.divider} />
                        <StatusHistory taskId={task.uuid} onRefresh={router.invalidate} />
                        <hr className={shared.divider} />
                        <InputFiles files={task.inputFiles} />
                        <hr className={shared.divider} />
                        <Artifacts files={task.artifacts} />
                    </Flex>
                </Section>

                {canImport && <SurveyImport task={task} />}

                <CrossTabulationSettings taskTypeId={task.type.id} config={task.config} />
            </Page.Content>
        </Page.Root>
    );
}

function SystemShowReportingTaskRouteSkeleton() {
    const { new: isNewTask } = Route.useSearch();

    return (
        <Page.Root>
            <Page.Header title="タスク詳細" />

            <Page.Content gap="5">
                <Page.BackLink to="/system/reporting" label="タスク一覧に戻る" forceNavigate={isNewTask} />

                <Section>
                    <Flex direction="column" gap="5">
                        <TaskOverviewSkeleton />
                        <hr className={shared.divider} />
                        <StatusHistorySkeleton />
                        <hr className={shared.divider} />
                        <InputFilesSkeleton />
                        <hr className={shared.divider} />
                        <ArtifactsSkeleton />
                    </Flex>
                </Section>

                <CrossTabulationSettingsSkeleton />
            </Page.Content>
        </Page.Root>
    );
}
